import { apiService } from "./apiService";
import {
  IRequestCases,
  IResponseCases,
  IResponseCount,
  initialResponseCount,
} from "../types/case/searchCase";
import { ICase, IClosedCasesToBulk } from "../types/case/case";
import {
  timestampToMoment,
  momentToMiliseconds,
  formatCapitalized,
} from "../utils/utils";

export interface ISaveImageReq {
  caseId: string;
  id: string;
  providers: any;
  imageId: number;
  image: string;
  type: "Child" | "Abductor";
}
export interface ISaveMiscImageReq {
  caseId: string;
  imageId: string;
  image: string;
}
export interface ICaseCenter {
  caseId: string;
  agencyCode: string;
  center: [number, number];
}
export const CasesService = {
  save: (newCase: any) => {
    const caseDB = CasesService.formatCaseDB(newCase);
    caseDB.createdDate = new Date().getTime();
    caseDB.miscellaneous = {};
    return apiService.post("/api/cases", { newCase: caseDB, refresh: true });
  },
  update: (beforeCase: any, afterCase: any) => {
    const before = CasesService.formatCaseDB(beforeCase, true);
    const beforeMiscellaneous = {};
    before.miscellaneous.forEach(
      (miscellaneous) => (beforeMiscellaneous[miscellaneous] = true)
    );
    before.miscellaneous = beforeMiscellaneous;
    const after = CasesService.formatCaseDB(afterCase, true);
    after.miscellaneous = beforeMiscellaneous;
    return apiService.put("/api/cases", { updateCase: { before, after } });
  },
  delete: (caseId) => {
    return apiService.delete("/api/cases", {
      data: { deleteCase: { caseId } },
    });
  },
  uploadImage: (settings: ISaveImageReq) => {
    return apiService
      .post("/api/cases/image", { case: settings })
      .then((response) => {
        return response.data.success ? true : false;
      });
  },
  uploadMiscImage: (settings: ISaveMiscImageReq) => {
    return apiService
      .post("/api/cases/misc", { case: settings })
      .then((response) => {
        return response.data.success ? true : false;
      });
  },
  removeMiscImage: (caseId: string, imageId: string) => {
    return apiService.delete("/api/cases/misc", {
      data: { deleteImage: { caseId, imageId } },
    });
  },
  formatCaseDB: (newCase, update?: boolean) => {
    const caseDB = { ...newCase };
    caseDB.openDate = momentToMiliseconds(caseDB.openDate);
    caseDB.caseId = `${caseDB.agencyCode}-${caseDB.caseId}`;
    caseDB.children = CasesService.formatPersonDB(caseDB, "children", update);
    caseDB.abductors = CasesService.formatPersonDB(caseDB, "abductors", update);
    if (caseDB.missingDate) {
      caseDB.missingDate = momentToMiliseconds(caseDB.missingDate);
    }
    if (caseDB.closedDate) {
      caseDB.closedDate = new Date(caseDB.closedDate).getTime();
    }
    CasesService.removeUnusedProperties(caseDB);
    return caseDB;
  },
  formatPersonDB: (
    caseDB,
    type: "children" | "abductors",
    update?: boolean
  ) => {
    return caseDB[type]
      ? caseDB[type].map((personFE) => {
        const personDB = { ...personFE };
        if (personDB.birthDate) {
          personDB.birthDate = momentToMiliseconds(personDB.birthDate);
          delete personDB.age;
        }
        personDB.fullName = formatCapitalized(personDB.fullName);
        CasesService.removeUnusedProperties(personDB);
        CasesService.removeUnitProperties(personDB);
        if (!update) {
          delete personDB.images;
        } else {
          personDB.images = CasesService.formatImagesBE(personDB.images);
        }
        return personDB;
      })
      : [];
  },
  removeUnusedProperties: (object: any) => {
    Object.keys(object).forEach((key) => {
      const value = object[key];
      if (
        (typeof value === "string" && value.length === 0) ||
        value === undefined
      ) {
        delete object[key];
      }
    });
  },
  removeUnitProperties: (person: any) => {
    if (person.weight === "" || person.weight === undefined) {
      delete person.weightUnit;
    }
    if (person.height === "" || person.height === undefined) {
      delete person.heightUnit;
    }
  },
  getCases: (request: IRequestCases): Promise<IResponseCases> => {
    return apiService
      .post("/api/cases/search", { request })
      .then((res) => {
        const { data } = res;
        return data as IResponseCases;
      })
      .then(formatIndexedCases);
  },
  autocompleteSearchCases: (request: {
    search: string;
    agency: string;
  }): Promise<IResponseCases> => {
    return apiService
      .post("/api/cases/searchCaseId", { request })
      .then((res) => {
        const { data } = res;
        const {
          cases: { total, results },
        } = data as IResponseCases;
        return {
          cases: {
            total,
            results: results.map((caseData) => {
              caseData.children = CasesService.formatPersonFE(
                caseData,
                "children"
              );
              return caseData;
            }),
          },
        } as IResponseCases;
      })
      .then(formatIndexedCases);
  },
  formatCaseFE: (caseDB) => {
    const caseFE = { ...caseDB };
    const { caseId } = caseFE;
    const index = caseId.indexOf("-");
    caseFE.agencyCode = caseId.substr(0, index);
    caseFE.caseId = caseId.substr(index + 1);
    caseFE.openDate = timestampToMoment(caseFE.openDate);
    if (caseFE.missingDate) {
      caseFE.missingDate = timestampToMoment(caseFE.missingDate);
    }
    caseFE.children = CasesService.formatPersonFE(caseFE, "children");
    caseFE.abductors = CasesService.formatPersonFE(caseFE, "abductors");
    caseFE.miscellaneous = caseDB.miscellaneous
      ? Object.keys(caseDB.miscellaneous)
      : [];
    return caseFE;
  },
  formatPersonFE: (caseFE, type: "children" | "abductors") => {
    return caseFE[type]
      ? caseFE[type].map((personDB) => {
        const personFE = { ...personDB };
        personFE.images = CasesService.formatImagesFe(personFE.images);
        if (personDB.birthDate) {
          personFE.birthDate = timestampToMoment(personFE.birthDate);
        }
        return personFE;
      })
      : [];
  },
  formatImagesFe: (images: {
    portrait?: string;
    alternate?: string;
    progressed?: string;
  }) => {
    const names = ["portrait", "alternate", "progressed"];
    return names.map((name) => {
      return (images && images[name]) || "";
    });
  },
  formatImagesBE: (images: string[]) => {
    const names = ["portrait", "alternate", "progressed"];
    return images.reduce((object, url, key) => {
      object[names[key]] = url.indexOf("base64") > 0 || url === "" ? null : url;
      return object;
    }, {});
  },
  getCaseByID: (caseId: string): Promise<ICase> => {
    return apiService.get(`/api/cases/${caseId}`).then((res) => {
      const { data } = res;
      if (res.status === 200 && data.case) {
        return CasesService.formatCaseFE(data.case) as ICase;
      }
      throw { error: "no data" };
    });
  },
  exists: (caseId: string): Promise<ICase> => {
    return apiService.get(`/api/cases/exists/${caseId}`).then((res) => {
      const { data } = res;
      return (data && data.exists) || false;
    });
  },
  getCount: (): Promise<IResponseCount> => {
    return apiService
      .get(`/api/cases/count/data`)
      .then((res) => {
        const { data } = res;
        if (!data.count) {
          throw { error: true };
        }
        return data.count;
      })
      .catch(() => initialResponseCount);
  },
  getClosedByRange: (range: { gte: number; lte: number }) => {
    return apiService
      .post("/api/cases/closeReason", { range })
      .then((res) => res.data.closedCases);
  },
  bulkUpdateClosed: (updatedCases: IClosedCasesToBulk) => {
    return apiService
      .put("/api/cases/closeReason", { ...updatedCases })
      .then((res) => (res.data.success ? true : false));
  },
};
const formatIndexedCases = (cases: IResponseCases) => {
  const results = cases.cases.results.map((caseInfo) => {
    caseInfo.birthDate = timestampToMoment(caseInfo.birthDate);
    caseInfo.missingSince = timestampToMoment(caseInfo.missingSince);
    caseInfo.openDate = timestampToMoment(caseInfo.openDate);
    return caseInfo;
  });
  cases.cases.results = results;
  return cases;
};
